import {
  accountSearchColumns,
  khachHangSearchColumns,
  MA_CHUNG_TU,
  mauSoHoaDonSearchColumns,
  nhanVienSearchColumns,
  QUERY_KEYS
} from '@/constants';
import { AritoIcon, CurrencyInput, DocumentNumberField, FormField, SearchField } from '@/components/custom/erp';
import { AccountModel, KhachHang, MauSoHoaDon, NhanVien } from '@/types/schemas';
import { useDetailSelection } from '@/hooks/use-detail-selection';
import { FormFieldActions, FormFieldState } from '../../hooks';
import { Label } from '@/components/ui/label';
import { FormMode } from '@/types/form';

interface BasicInfoTabProps {
  formMode: FormMode;
  formState: {
    state: FormFieldState;
    actions: FormFieldActions;
  };
}

export default function BasicInfoTab({ formMode, formState: { state, actions } }: BasicInfoTabProps) {
  const handleAccountSelection = useDetailSelection<AccountModel>({
    endpoint: QUERY_KEYS.TAI_KHOAN,
    onSelect: actions.setTaiKhoan
  });

  return (
    <div className='flex flex-col gap-3 p-4'>
      {/* Customer Information Section */}
      <div className='grid grid-cols-12 gap-6'>
        {/* Left Column */}
        <div className='col-span-6 space-y-1'>
          <div className='flex items-center'>
            <Label className='w-36 font-medium'>Mã khách hàng</Label>
            <SearchField<KhachHang>
              type='text'
              value={state.khachHang?.customer_code || ''}
              searchEndpoint={`/${QUERY_KEYS.KHACH_HANG}/`}
              searchColumns={khachHangSearchColumns}
              onRowSelection={actions.setKhachHang}
              columnDisplay='customer_code'
              dialogTitle='Danh mục khách hàng'
              className='w-48'
              disabled={formMode === 'view'}
            />

            <Label className='w-20 font-medium'>Mã số thuế</Label>
            <FormField
              name='ma_so_thue'
              type='text'
              disabled={formMode === 'view'}
              placeholder='Nhập và tra cứu'
              value={state.khachHang?.tax_code || ''}
              className='w-48'
            />
            {/* <button className='ml-2 flex items-center justify-center border border-gray-200 px-3 py-1.5'>
                <AritoIcon icon={15} className='shrink-0' />
              </button>
              <button className='ml-2 flex items-center justify-center border border-gray-200 px-3 py-1.5'>
                <AritoIcon icon={888} />
              </button> */}
          </div>

          <div className='flex items-center'>
            <Label className='w-36 font-medium'>Tên khách/Ng.mua</Label>
            <div className='flex-1'>
              <FormField
                name='ten_kh_thue'
                type='text'
                disabled={formMode === 'view'}
                value={state.khachHang?.customer_name || ''}
                placeholder='Nhập tên nhà cung cấp/đơn vị'
              />
            </div>
          </div>

          <div className='flex items-center'>
            <Label className='w-36 font-medium'>Địa chỉ</Label>
            <div className='flex-1'>
              <FormField
                name='dia_chi'
                type='text'
                disabled={formMode === 'view'}
                labelClassName='w-48'
                value={state.khachHang?.address || ''}
                placeholder='Nhập địa chỉ nhà cung cấp'
              />
            </div>
          </div>

          <div className='flex items-center'>
            <Label className='w-36 font-medium'>Tài khoản có</Label>
            <SearchField<AccountModel>
              type='text'
              value={state.khachHang?.account_data?.code || state.taiKhoan?.code || ''}
              relatedFieldValue={state.khachHang?.account_data?.name || state.taiKhoan?.name || ''}
              searchEndpoint={`/${QUERY_KEYS.TAI_KHOAN}/`}
              columnDisplay='code'
              displayRelatedField='name'
              searchColumns={accountSearchColumns}
              dialogTitle='Danh sách tài khoản'
              onRowSelection={handleAccountSelection}
              disabled={formMode === 'view'}
            />
          </div>
          <div className='flex items-center'>
            <Label className='w-36 font-medium'>Mẫu hóa đơn</Label>
            <SearchField<MauSoHoaDon>
              value={state.mauHoaDon?.ma_mau_so || ''}
              relatedFieldValue={state.mauHoaDon?.ten_mau_so || ''}
              searchEndpoint={`/${QUERY_KEYS.MAU_SO_HOA_DON}/`}
              columnDisplay='ma_mau_so'
              displayRelatedField='ten_mau_so'
              searchColumns={mauSoHoaDonSearchColumns}
              dialogTitle='Danh mục mẫu chứng từ'
              onRowSelection={actions.setMauHoaDon}
              disabled={formMode === 'view'}
            />
          </div>
          <div className='flex items-center'>
            <Label className='w-36 font-medium'>Mã nhân viên</Label>
            <SearchField<NhanVien>
              value={state.khachHang?.sales_rep_data?.ma_nhan_vien || state.nhanVien?.ma_nhan_vien || ''}
              relatedFieldValue={
                state.khachHang?.sales_rep_data?.ho_ten_nhan_vien || state.nhanVien?.ho_ten_nhan_vien || ''
              }
              searchEndpoint={`/${QUERY_KEYS.NHAN_VIEN}/`}
              columnDisplay='ma_nhan_vien'
              displayRelatedField='ho_ten_nhan_vien'
              searchColumns={nhanVienSearchColumns}
              dialogTitle='Danh mục nhân viên'
              onRowSelection={actions.setNhanVien}
              disabled={formMode === 'view'}
            />
          </div>

          <div className='flex items-center'>
            <Label className='w-36 font-medium'>Diễn giải</Label>
            <div className='flex-1'>
              <FormField name='dien_giai' disabled={formMode === 'view'} />
            </div>
          </div>
        </div>

        {/* Middle Column */}
        <div className='col-span-3 space-y-1'>
          <div className='ml-4 flex h-8 items-center'></div>

          <div className='ml-4 flex items-center'>
            <FormField
              name='ong_ba'
              label='Người nhận'
              labelClassName='font-medium w-32'
              disabled={formMode === 'view'}
              className='w-52'
              value={state.khachHang?.contact_person || ''}
              placeholder='Nhập tên người nhận'
            />
          </div>

          <div className='ml-4 flex items-center'>
            <FormField
              name='e_mail'
              label='Email'
              labelClassName='font-medium w-32'
              disabled={formMode === 'view'}
              className='w-52'
              value={state.khachHang?.email || ''}
              placeholder='Nhập email'
            />
          </div>
        </div>

        {/* Right Column */}
        <div className='col-span-3 flex'>
          {/* Tab Content */}
          <div className='flex-1 pr-2'>
            <DocumentNumberField
              ma_ct={MA_CHUNG_TU.BAN_HANG.PHIEU_NHAP_HANG_BAN_TRA_LAI}
              quyenChungTu={state.quyenChungTu}
              onQuyenChungTuChange={actions.setQuyenChungTu}
              soChungTu={state.soChungTu}
              onSoChungTuChange={actions.setSoChungTu}
              disabled={formMode === 'view'}
              classNameSearchField='w-full'
              labelClassName='font-medium font-medium w-32'
              formMode={formMode}
            />

            <div className='flex items-center'>
              <Label className='w-32 font-medium'>Ngày chứng từ</Label>
              <div className='flex-1'>
                <FormField name='ngay_ct' type='date' disabled={formMode === 'view'} />
              </div>
            </div>
            <div className='flex items-center'>
              <Label className='w-32 font-medium'>Số hóa đơn</Label>
              <div className='flex-1'>
                <FormField name='so_ct0' placeholder='SỐ HÓA ĐƠN' type='text' disabled={formMode === 'view'} />
              </div>
            </div>
            <div className='flex items-center'>
              <Label className='w-32 font-medium'>Ký hiệu</Label>
              <div className='flex-1'>
                <FormField name='so_ct2' placeholder='KÝ HIỆU' type='text' disabled={formMode === 'view'} />
              </div>
            </div>
            <div className='flex items-center'>
              <Label className='w-32 font-medium'>Ngày hoá đơn</Label>
              <div className='flex-1'>
                <FormField name='ngay_ct0' type='date' disabled={formMode === 'view'} />
              </div>
            </div>

            <CurrencyInput formMode={formMode} classNameInput='w-full' labelClassName='font-medium w-[115px]' />

            <div className='flex items-center'>
              <Label className='w-32 font-medium'>Trạng thái</Label>
              <div className='flex-1'>
                <FormField
                  name='status'
                  type='select'
                  disabled={formMode === 'view'}
                  options={[
                    { value: '0', label: 'Lập chứng từ' },
                    { value: '3', label: 'Chờ duyệt' },
                    { value: '5', label: 'Trả lại' }
                  ]}
                />
              </div>
            </div>

            <div className='mt-4 flex'>
              <div className='mb-4 h-2 w-32 shrink-0' />
              <FormField
                label='Dữ liệu được nhận'
                name='transfer_yn'
                type='checkbox'
                disabled={true}
                labelClassName='font-medium w-32'
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
